import { forwardRef, type ComponentProps, type InputHTMLAttributes, type ReactNode, type TextareaHTMLAttributes } from 'react';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@unitfield/ui/components/ui/input-otp';
import { UnitfieldIcon } from '@unitfield/ui/icons';
import { cn } from '@unitfield/ui/lib/utils';
import styles from './fields.module.css';

export { styles as fieldStyles };

/** A label over its control, with an optional hint and an error that names itself for aria. */
export function Field({
  label,
  htmlFor,
  optional = false,
  hint,
  error,
  errorId,
  className,
  children,
}: {
  label: ReactNode;
  htmlFor?: string;
  optional?: boolean;
  hint?: ReactNode;
  error?: string | null;
  errorId?: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className={cn(styles.field, className)}>
      <label className={styles.label} htmlFor={htmlFor}>
        {label}
        {optional ? <span className={styles.optional}>Optional</span> : null}
      </label>
      {children}
      {hint ? <p className={cn(styles.hint, 'num')}>{hint}</p> : null}
      <FieldError id={errorId} message={error} />
    </div>
  );
}

export function FieldError({ id, message }: { id?: string; message?: string | null }) {
  if (!message) return null;
  return <p id={id} role="alert" className={styles.error}>{message}</p>;
}

export interface TextWellProps extends InputHTMLAttributes<HTMLInputElement> {
  /** Shown before the value, e.g. "$". */
  leading?: ReactNode;
  invalid?: boolean;
  wellClassName?: string;
}

/** A filled well around a native input; the focus ring is the brand, on the well's own radius. */
export const TextWell = forwardRef<HTMLInputElement, TextWellProps>(function TextWell(
  { leading, invalid, wellClassName, className, disabled, ...props },
  ref,
) {
  return (
    <span className={cn(styles.well, wellClassName)} data-invalid={invalid ? '' : undefined} data-disabled={disabled ? '' : undefined}>
      {leading ? <span className={styles.prefix} aria-hidden="true">{leading}</span> : null}
      <input ref={ref} {...props} className={cn(styles.input, className)} disabled={disabled} aria-invalid={invalid || props['aria-invalid'] || undefined} />
    </span>
  );
});

export function TextAreaWell({ invalid, className, ...props }: TextareaHTMLAttributes<HTMLTextAreaElement> & { invalid?: boolean }) {
  return (
    <span className={cn(styles.well, styles.textarea)} data-invalid={invalid ? '' : undefined}>
      <textarea className={cn(styles.input, className)} aria-invalid={invalid || undefined} {...props} />
    </span>
  );
}

/** A native checkbox drawn as an ink box, so keyboard, forms and tests see a real checkbox. */
export function CheckRow({
  checked,
  disabled,
  onChange,
  children,
  className,
}: {
  checked: boolean;
  disabled?: boolean;
  onChange: (checked: boolean) => void;
  children: ReactNode;
  className?: string;
}) {
  return (
    <label className={cn(styles.check, className)}>
      <input type="checkbox" checked={checked} disabled={disabled} onChange={(event) => onChange(event.target.checked)} />
      <span className={styles.box} aria-hidden="true">
        <UnitfieldIcon name="check" size={13} strokeWidth={2.6} />
      </span>
      <span>{children}</span>
    </label>
  );
}

/** One choice among a few, as pills. The chosen pill is ink, the rest are wells. */
export function ChipChoice<T extends string>({
  label,
  value,
  options,
  onChange,
  disabled,
  invalid,
  describedBy,
  className,
}: {
  label: string;
  value: string | null | undefined;
  options: ReadonlyArray<readonly [T, string]>;
  onChange: (value: T) => void;
  disabled?: boolean;
  invalid?: boolean;
  describedBy?: string;
  className?: string;
}) {
  return (
    <div role="radiogroup" aria-label={label} aria-invalid={invalid || undefined} aria-describedby={describedBy} className={cn(styles.chips, className)}>
      {options.map(([option, text]) => (
        <button
          key={option}
          type="button"
          role="radio"
          aria-checked={value === option}
          className={styles.chip}
          disabled={disabled}
          onClick={() => onChange(option)}
        >
          {text}
        </button>
      ))}
    </div>
  );
}

/** Six code wells, as in sign-up. The input itself stays the shared one-time-code input. */
export function CodeSlots({ invalid, ...props }: Omit<ComponentProps<typeof InputOTP>, 'children' | 'render' | 'maxLength'> & { invalid?: boolean }) {
  return (
    <InputOTP maxLength={6} containerClassName={styles.slots} {...props}>
      <InputOTPGroup className={styles.slotGroup}>
        {Array.from({ length: 6 }, (_, index) => (
          <InputOTPSlot key={index} index={index} aria-invalid={invalid || undefined} />
        ))}
      </InputOTPGroup>
    </InputOTP>
  );
}
